import { Typography } from '@mui/material';
import CurrencyRate from 'components/CurrencyRate';
import { Column } from 'further-ui/components/Layout';
import { CurrencyRateDetails } from 'further-types/lp/transaction';
import { CurrencyCode, FxDataFeed } from 'further-types/lp/vehicle';
import { CalendarDay } from 'further-ui/utils';
import useCurrencyRate from 'hooks/lp/data/currencies/useCurrencyRate';
import React from 'react';

type BulkFxRateRowProps = {
  currency: CurrencyCode;
  vehicle: {
    _id: string;
    baseCurrency: CurrencyCode;
    fxDataFeed: FxDataFeed;
  };
  date: CalendarDay;
  currentRate?: CurrencyRateDetails;
  onRateChange: (currency: CurrencyCode, rate: CurrencyRateDetails) => void;
};

const BulkFxRateRow: React.FC<BulkFxRateRowProps> = ({
  currency,
  vehicle,
  date,
  currentRate,
  onRateChange,
}) => {
  const { fxRate, changeRate, isFetchingRate } = useCurrencyRate({
    vehicleId: vehicle._id,
    currency,
    date,
    defaultCurrencyRate: currentRate ?? null,
    onRateChange: (rate) => {
      if (rate) {
        onRateChange(currency, rate);
      }
    },
  });

  return (
    <CurrencyRate
      currency={currency}
      date={date}
      vehicle={vehicle}
      fxRate={fxRate}
      onRateChange={(rateDetails) => {
        changeRate(rateDetails);
      }}
      isFetchingRate={isFetchingRate}
    />
  );
};

type BulkFxRatesProps = {
  currencies: Array<CurrencyCode>;
  vehicle: {
    _id: string;
    baseCurrency: CurrencyCode;
    fxDataFeed: FxDataFeed;
  };
  date: CalendarDay;
  globalFxRates: Record<string, CurrencyRateDetails>;
  onRateChange: (currency: CurrencyCode, rate: CurrencyRateDetails) => void;
};

const BulkFxRates: React.FC<BulkFxRatesProps> = ({
  currencies,
  vehicle,
  date,
  globalFxRates,
  onRateChange,
}) => {
  if (currencies.length === 0) return null;

  return (
    <Column spacing="xs">
      <Typography variant="subtitle2">Exchange rates</Typography>
      {currencies.map((currency) => (
        <BulkFxRateRow
          key={currency}
          currency={currency}
          vehicle={vehicle}
          date={date}
          currentRate={globalFxRates[currency]}
          onRateChange={onRateChange}
        />
      ))}
    </Column>
  );
};

export default BulkFxRates;
